import {use, useCallback} from 'react'
import {useCAPIQuery} from '@github-ui/copilot-shared-ui/capi/use-capi-query'
import {useQueryClient} from '@github-ui/react-query'
import {useAgentSessionsAlive, type AgentSessionAlivePayload} from './use-agent-sessions-alive'
import {useDebounce} from '@github-ui/use-debounce'
import {fetchAllEvents, generateEventsQueryKey, type EventsQueryData} from '../utils/fetch-session-events'
import {buildConversationModel} from '../utils/build-conversation-model'
import {SessionContext} from '../contexts/SessionContext'
import {isConversationRefused} from '../utils/conversation-access'
import {useIsAhpSession} from './use-is-ahp-session'
import type {ConversationModel, SessionEvent} from '../types/events'
import type {Command} from '../types/session'

export interface SessionEventsData {
  events: SessionEvent[]
  total: number
  model: ConversationModel
}

export interface UseSessionEventsQueryOptions {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** Whether to enable the query */
  enabled?: boolean
  /** Refetch interval in milliseconds, or false to disable polling */
  refetchInterval?: number | false
  /** Commands to feed into the conversation model for dedup and synthetic pending turns */
  commands?: Command[]
}

export interface UseSessionEventsOptions {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** Whether to enable the query */
  enabled?: boolean
  /** Polling interval in milliseconds (used as fallback when Alive is not connected) */
  pollingInterval?: number
  /** Commands to feed into the conversation model for dedup and synthetic pending turns */
  commands?: Command[]
}

export function isEventUpdateForTask(
  payload: AgentSessionAlivePayload | undefined,
  taskId: string | undefined,
): boolean {
  return !payload?.task_id || payload.task_id === taskId
}

/**
 * Low-level hook for reading session events that skips Alive subscriptions and polling.
 */
export function useSessionEventsQuery({
  taskId,
  enabled = true,
  refetchInterval = false,
  commands,
}: UseSessionEventsQueryOptions) {
  const queryClient = useQueryClient()
  const queryKey = generateEventsQueryKey(taskId)

  const query = useCAPIQuery<EventsQueryData, SessionEventsData>({
    queryKey,
    queryFn: async () => {
      if (!taskId) throw new Error('No task ID provided')
      const qk = generateEventsQueryKey(taskId)
      const prev = queryClient.getQueryData<EventsQueryData>(qk)
      return fetchAllEvents(taskId, prev)
    },
    select: (d: EventsQueryData) => ({
      events: d.events,
      total: d.total,
      model: buildConversationModel(d.events, commands),
    }),
    // A refused conversation stays refused until the viewer's access changes,
    // and every read costs the resume gate a dotcom authorization call.
    refetchInterval: current => (isConversationRefused(current.state.error) ? false : refetchInterval),
    enabled: enabled && !!taskId,
  })

  return query
}

/**
 * Hook that fetches and accumulates session events for a task.
 *
 * Default path: Alive websocket push notifications trigger cursor-based
 * incremental REST loading, falling back to polling when Alive is disconnected.
 *
 * Loads session events in the Copilot SDK events format from Mission Control.
 */
export function useSessionEvents({taskId, enabled = true, pollingInterval = 3000, commands}: UseSessionEventsOptions) {
  const queryClient = useQueryClient()

  const sessionContext = use(SessionContext)
  const session = sessionContext?.session ?? null
  const {isAhpSession} = useIsAhpSession(session)

  // AHP sessions are never rendered through the REST events cache (see above).
  // `isAhpSession` resolves synchronously from the session's environment id, so
  // an AHP session never fetches REST — and a non-environment (regular cloud)
  // session is never gated on the environments query.
  const restEnabled = enabled && !!taskId && !isAhpSession

  const handleEventUpdate = useCallback(
    (payload?: AgentSessionAlivePayload) => {
      if (!isEventUpdateForTask(payload, taskId)) return
      queryClient.invalidateQueries({queryKey: generateEventsQueryKey(taskId)})
    },
    [queryClient, taskId],
  )

  const throttledHandleEventUpdate = useDebounce(handleEventUpdate, pollingInterval, {
    leading: true,
    trailing: true,
    maxWait: pollingInterval,
  })

  const {isConnected: aliveIsConnected} = useAgentSessionsAlive({
    onEvent: throttledHandleEventUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: false,
    enableLogUpdates: true,
    enabled: restEnabled,
  })

  const query = useSessionEventsQuery({
    taskId,
    enabled: restEnabled,
    refetchInterval: aliveIsConnected ? false : pollingInterval,
    commands,
  })

  return {
    ...query,
    aliveIsConnected,
  }
}
